import React, { useEffect, useState } from "react";
import { useLanguage } from "@/contexts/LanguageContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { CheckCircle2, XCircle, KeyRound, Mail } from "lucide-react";

const StatusPill = ({ ok, label }) => (
  <span
    className={`inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-md border ${
      ok ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-slate-50 text-slate-500 border-slate-200"
    }`}
  >
    {ok ? <CheckCircle2 className="w-3.5 h-3.5" /> : <XCircle className="w-3.5 h-3.5" />}
    {label}
  </span>
);

export const IntegrationsTab = () => {
  const { lang, t } = useLanguage();
  const [view, setView] = useState(null);
  const [moyasarMode, setMoyasarMode] = useState("test");
  const [tpub, setTpub] = useState("");
  const [tsec, setTsec] = useState("");
  const [lpub, setLpub] = useState("");
  const [lsec, setLsec] = useState("");
  const [whsec, setWhsec] = useState("");
  const [resendKey, setResendKey] = useState("");
  const [fromEmail, setFromEmail] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/integrations");
      setView(data);
      setMoyasarMode(data.moyasar.mode);
      setFromEmail(data.resend.from_email || "");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  useEffect(() => { load(); }, []);

  const saveMoyasar = async () => {
    setBusy(true);
    try {
      const payload = { mode: moyasarMode };
      if (tpub) payload.test_publishable = tpub;
      if (tsec) payload.test_secret = tsec;
      if (lpub) payload.live_publishable = lpub;
      if (lsec) payload.live_secret = lsec;
      if (whsec) payload.webhook_secret = whsec;
      const { data } = await api.put("/admin/integrations/moyasar", payload);
      setView(data);
      setTpub(""); setTsec(""); setLpub(""); setLsec(""); setWhsec("");
      toast.success(lang === "ar" ? "تم الحفظ" : "Saved");
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  const saveResend = async () => {
    setBusy(true);
    try {
      const payload = {};
      if (resendKey !== "") payload.api_key = resendKey;
      if (fromEmail) payload.from_email = fromEmail;
      const { data } = await api.put("/admin/integrations/resend", payload);
      setView(data);
      setResendKey("");
      toast.success(lang === "ar" ? "تم الحفظ" : "Saved");
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  const clearResend = async () => {
    setBusy(true);
    try {
      const { data } = await api.put("/admin/integrations/resend", { api_key: "" });
      setView(data);
      toast.success(lang === "ar" ? "تم المسح" : "Cleared");
    } finally { setBusy(false); }
  };

  if (!view) return <p className="text-sm text-slate-500">{t("loading")}</p>;

  return (
    <div className="space-y-6">
      <Alert className="bg-slate-50 border-slate-200">
        <AlertDescription className="text-xs text-slate-600 leading-relaxed">
          {lang === "ar"
            ? "جميع المفاتيح تُحفظ مشفّرة في قاعدة البيانات (Fernet) ولا تُعرض إلا بصيغة مخفية. يكفي إدخال قيمة جديدة للاستبدال، أو ترك الحقل فارغاً للإبقاء على الحالي. لإلغاء مفتاح اكتب مسافة واحدة ثم احفظ."
            : "All keys are stored Fernet-encrypted in the database and only shown masked. Enter a new value to replace, or leave blank to keep current. To clear, type a single space and save."}
        </AlertDescription>
      </Alert>

      {/* Moyasar */}
      <Card className="p-6 border border-slate-200 shadow-none">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <KeyRound className="w-5 h-5 text-slate-700" />
            <h3 className="font-heading text-lg font-bold text-slate-900">Moyasar</h3>
          </div>
          <div className="flex items-center gap-3">
            <StatusPill ok={view.moyasar.test.configured} label={lang === "ar" ? "اختبار" : "Test"} />
            <StatusPill ok={view.moyasar.live.configured} label={lang === "ar" ? "إنتاج" : "Live"} />
            <StatusPill ok={view.moyasar.webhook_secret_configured} label="Webhook" />
          </div>
        </div>

        <div className="flex items-center justify-between bg-slate-50 border border-slate-200 rounded-md p-3 mb-5" data-testid="moyasar-mode-toggle">
          <span className="text-sm">
            {lang === "ar" ? "وضع الإنتاج (Live)" : "Live mode"}
            <span className="ms-2 text-xs text-slate-500">
              ({lang === "ar" ? "حالياً:" : "current:"} <b>{moyasarMode}</b>)
            </span>
          </span>
          <Switch
            checked={moyasarMode === "live"}
            onCheckedChange={(v) => setMoyasarMode(v ? "live" : "test")}
            data-testid="moyasar-live-switch"
          />
        </div>

        <div className="grid sm:grid-cols-2 gap-5">
          <div className="space-y-2">
            <Label className="text-start block">
              {lang === "ar" ? "مفتاح عام للاختبار" : "Test Publishable"} <span className="text-xs text-slate-400">{view.moyasar.test.publishable_masked || "—"}</span>
            </Label>
            <Input value={tpub} onChange={(e) => setTpub(e.target.value)} placeholder="pk_test_..." data-testid="moyasar-test-pub" />
          </div>
          <div className="space-y-2">
            <Label className="text-start block">
              {lang === "ar" ? "مفتاح سري للاختبار" : "Test Secret"} <span className="text-xs text-slate-400">{view.moyasar.test.secret_masked || "—"}</span>
            </Label>
            <Input type="password" value={tsec} onChange={(e) => setTsec(e.target.value)} placeholder="sk_test_..." data-testid="moyasar-test-sec" />
          </div>
          <div className="space-y-2">
            <Label className="text-start block">
              {lang === "ar" ? "مفتاح عام للإنتاج" : "Live Publishable"} <span className="text-xs text-slate-400">{view.moyasar.live.publishable_masked || "—"}</span>
            </Label>
            <Input value={lpub} onChange={(e) => setLpub(e.target.value)} placeholder="pk_live_..." data-testid="moyasar-live-pub" />
          </div>
          <div className="space-y-2">
            <Label className="text-start block">
              {lang === "ar" ? "مفتاح سري للإنتاج" : "Live Secret"} <span className="text-xs text-slate-400">{view.moyasar.live.secret_masked || "—"}</span>
            </Label>
            <Input type="password" value={lsec} onChange={(e) => setLsec(e.target.value)} placeholder="sk_live_..." data-testid="moyasar-live-sec" />
          </div>
          <div className="space-y-2 sm:col-span-2">
            <Label className="text-start block">
              {lang === "ar" ? "سرّ Webhook" : "Webhook secret"} <span className="text-xs text-slate-400">{view.moyasar.webhook_secret_masked || "—"}</span>
            </Label>
            <Input type="password" value={whsec} onChange={(e) => setWhsec(e.target.value)} placeholder="any-random-string" data-testid="moyasar-webhook" />
            <p className="text-[11px] text-slate-500">
              {lang === "ar" ? "اضبط في Moyasar Dashboard على رابط webhook: " : "Register this in Moyasar dashboard pointing to: "}
              <code className="text-[11px]">{process.env.REACT_APP_BACKEND_URL}/api/payments/moyasar/webhook?token=YOUR_SECRET</code>
            </p>
          </div>
        </div>

        <div className="flex justify-end mt-5">
          <Button onClick={saveMoyasar} disabled={busy} className="bg-slate-900 hover:bg-slate-800" data-testid="moyasar-save-btn">
            {busy ? t("loading") : t("save_btn")}
          </Button>
        </div>
      </Card>

      {/* Resend */}
      <Card className="p-6 border border-slate-200 shadow-none">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Mail className="w-5 h-5 text-slate-700" />
            <h3 className="font-heading text-lg font-bold text-slate-900">Resend</h3>
          </div>
          <StatusPill ok={view.resend.configured} label={lang === "ar" ? "مُهيّأ" : "Configured"} />
        </div>

        <div className="space-y-5">
          <div className="space-y-2">
            <Label className="text-start block">
              Resend API Key <span className="text-xs text-slate-400">{view.resend.api_key_masked || "—"}</span>
            </Label>
            <Input
              type="password"
              value={resendKey}
              onChange={(e) => setResendKey(e.target.value)}
              placeholder="re_..."
              data-testid="resend-key"
            />
            <p className="text-[11px] text-slate-500">
              {lang === "ar"
                ? "إذا تركتَ المفتاح فارغاً، سيستمر النظام بالعمل بدون إرسال إيميلات (graceful no-op)."
                : "Leave empty to disable email sending (system continues to work — graceful no-op)."}
            </p>
          </div>
          <div className="space-y-2">
            <Label className="text-start block">From email</Label>
            <Input
              value={fromEmail}
              onChange={(e) => setFromEmail(e.target.value)}
              placeholder="Zabatna <no-reply@send.zabatna.com>"
              data-testid="resend-from"
            />
          </div>
        </div>

        <div className="flex justify-end mt-5 gap-2">
          <Button onClick={clearResend} variant="outline" disabled={busy} data-testid="resend-clear-btn">
            {lang === "ar" ? "مسح المفتاح" : "Clear key"}
          </Button>
          <Button onClick={saveResend} disabled={busy} className="bg-slate-900 hover:bg-slate-800" data-testid="resend-save-btn">
            {busy ? t("loading") : t("save_btn")}
          </Button>
        </div>
      </Card>
    </div>
  );
};

export default IntegrationsTab;
